import type { QueueTone } from '@/components/designSystem/QueueViews/types'
import { formatSlaDateTime } from '@/modules/company/Approvals/PriorityPayments/slaFormat'
import { DEFAULT_UTC_OFFSET, formatHourMinuteInOffset } from '@/utils/date'
import type {
  AffiliatePackageRow,
  AffiliatePackageUnit,
  AffiliatePayoutPolicy,
  AffiliateSlaState,
  AffiliateUnit
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

import { isPendingSlaState } from './labels'

/**
 * Regras de EXIBIÇÃO do portal. Regra de negócio (pertinência, prazo, repasse) é do
 * backend; aqui só se escreve texto. Nada de conta com dinheiro.
 */

const pad = (n: number): string => String(n).padStart(2, '0')

/** Brasília é -03:00 fixo (sem horário de verão desde 2019) — DEFAULT_UTC_OFFSET. */
const BRT_OFFSET_MS = 3 * 3_600_000

/** `HH:MM`; `+HH:MM` quando negativo (prazo estourado). */
export const formatHoursMinutes = (seconds: number): string => {
  const totalMinutes = Math.floor(Math.abs(seconds) / 60)
  const body = `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`
  return seconds < 0 ? `+${body}` : body
}

/** `8h36` · `9h` · `15 min`; `—` sem valor. */
export const formatDurationHours = (hours: number | null): string => {
  if (hours === null || !Number.isFinite(hours)) return '—'
  const totalMinutes = Math.round(hours * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (!h) return `${Math.max(m, 1)} min`
  return m ? `${h}h${pad(m)}` : `${h}h`
}

/** `"3.0000"` → `3%`; `"2.5000"` → `2,5%`; `—` para valor ausente ou inválido. */
export const formatPercent = (percent: string): string => {
  const value = typeof percent === 'string' && percent.trim() ? Number(percent) : NaN
  if (!Number.isFinite(value)) return '—'
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`
}

/** `YYYY-MM-DD` → `dd/mm`. */
export const formatDayMonth = (date: string): string => {
  const [, month, day] = date.slice(0, 10).split('-')
  return `${day}/${month}`
}

/** `YYYY-MM-DD` → `dd/mm/yyyy`. */
export const formatDateBR = (date: string): string => {
  const [year, month, day] = date.slice(0, 10).split('-')
  return `${day}/${month}/${year}`
}

const splitMonth = (month: string): [number, number] => {
  const [year, m] = month.split('-').map(Number)
  return [year, m]
}

export const todayBrt = (nowMs: number): string =>
  new Date(nowMs - BRT_OFFSET_MS).toISOString().slice(0, 10)

/** Meses de `to` até `from`, decrescente (`YYYY-MM`). */
export const monthRange = (from: string, to: string): string[] => {
  const months: string[] = []
  let [year, m] = splitMonth(to)
  const [fromYear, fromMonth] = splitMonth(from)
  while (year > fromYear || (year === fromYear && m >= fromMonth)) {
    months.push(`${year}-${pad(m)}`)
    m -= 1
    if (m === 0) {
      m = 12
      year -= 1
    }
  }
  return months
}

/** R2: "N de M plantões nas suas unidades" quando o pacote é misto. */
export const formatShiftCount = (
  row: Pick<AffiliatePackageRow, 'itemCount' | 'pertinentItemCount'>
): string => {
  if (row.pertinentItemCount < row.itemCount) {
    return `${row.pertinentItemCount} de ${row.itemCount} plantões nas suas unidades`
  }
  return `${row.itemCount} ${row.itemCount === 1 ? 'plantão' : 'plantões'}`
}

export const formatShiftRange = (first: string | null, last: string | null): string => {
  if (!first) return ''
  if (!last || last === first) return formatDayMonth(first)
  return `${formatDayMonth(first)} a ${formatDayMonth(last)}`
}

export const formatUnitNames = (units: AffiliatePackageUnit[]): string =>
  units.map((unit) => unit.name).join(', ')

export const formatSyncStamp = (iso: string | null, nowMs: number): string => {
  const ms = iso ? Date.parse(iso) : Number.NaN
  if (!iso || !Number.isFinite(ms)) return 'Ainda sem sincronização com o Antecipe'
  const minutes = Math.max(0, Math.floor((nowMs - ms) / 60_000))
  const relative =
    minutes < 1
      ? 'agora há pouco'
      : minutes < 60
        ? `há ${minutes} min`
        : `há ${formatDurationHours(minutes / 60)}`
  return `Atualizado ${relative} · ${formatHourMinuteInOffset(iso, DEFAULT_UTC_OFFSET) ?? '—'}`
}

export type PackageClockInput = Pick<
  AffiliatePackageRow,
  'stage' | 'slaState' | 'requestedAt' | 'deadlineAt' | 'paidAt' | 'paidAtApprox'
>

export interface PackageClock {
  value: string
  due: string
  tone: AffiliateSlaState
}

/** Estado "agora": pendente com prazo vencido no navegador vira OVERDUE. */
export const liveSlaState = (row: PackageClockInput, nowMs: number): AffiliateSlaState => {
  if (!isPendingSlaState(row.slaState) || row.paidAt) return row.slaState
  const deadlineMs = Date.parse(row.deadlineAt)
  return Number.isFinite(deadlineMs) && deadlineMs <= nowMs ? 'OVERDUE' : row.slaState
}

export const packageClock = (row: PackageClockInput, nowMs: number): PackageClock => {
  if (row.stage === 'CANCELED' || row.slaState === 'CANCELED') {
    return { value: '—', due: '', tone: 'CANCELED' }
  }
  // Expirado: prazo não corre; se o Antecipe reabrir, o ME devolve a etapa e um prazo novo.
  if (row.stage === 'EXPIRED' || row.slaState === 'EXPIRED') {
    return { value: '—', due: '', tone: 'EXPIRED' }
  }
  if (row.paidAt) {
    const seconds = (Date.parse(row.paidAt) - Date.parse(row.requestedAt)) / 1000
    return {
      value: Number.isFinite(seconds) ? formatHoursMinutes(seconds) : '—',
      due: `pago em ${formatSlaDateTime(row.paidAt)}${row.paidAtApprox ? ' (aprox.)' : ''}`,
      tone: row.slaState
    }
  }
  const deadlineMs = Date.parse(row.deadlineAt)
  if (!Number.isFinite(deadlineMs)) return { value: '—', due: '', tone: row.slaState }
  const remaining = (deadlineMs - nowMs) / 1000
  const deadline = formatSlaDateTime(row.deadlineAt)
  return {
    value: formatHoursMinutes(remaining),
    due: remaining < 0 ? `prazo era ${deadline}` : `prazo ${deadline}`,
    tone: liveSlaState(row, nowMs)
  }
}

/** Políticas cuja vigência toca o período da competência (datas inclusivas `YYYY-MM-DD`). */
export const policiesInPeriod = (
  policies: AffiliatePayoutPolicy[],
  periodStart: string,
  periodEnd: string
): AffiliatePayoutPolicy[] =>
  policies.filter(
    (policy) =>
      policy.validFrom <= periodEnd && (policy.validTo === null || policy.validTo >= periodStart)
  )

export const formatPolicyPeriod = (policy: Pick<AffiliatePayoutPolicy, 'validFrom' | 'validTo'>) =>
  policy.validTo
    ? `de ${formatDateBR(policy.validFrom)} a ${formatDateBR(policy.validTo)}`
    : `desde ${formatDateBR(policy.validFrom)}`

export const unitSituation = (
  unit: Pick<AffiliateUnit, 'validFrom' | 'validTo'>,
  today: string
): { label: string; tone: QueueTone } => {
  if (today < unit.validFrom)
    return { label: `Começa em ${formatDayMonth(unit.validFrom)}`, tone: 'warn' }
  if (unit.validTo && today > unit.validTo) {
    return { label: `Encerrado em ${formatDayMonth(unit.validTo)}`, tone: 'neutral' }
  }
  return { label: 'Ativa', tone: 'ok' }
}

/** "desde dd/mm" só quando o vínculo começou depois do início da competência exibida. */
export const unitSinceInPeriodLabel = (
  validFrom: string,
  periodStart: string,
  periodEnd: string
): string =>
  validFrom > periodStart && validFrom <= periodEnd ? `desde ${formatDayMonth(validFrom)}` : ''

export const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return ''
  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return `${first}${last}`.toUpperCase()
}

/** "#A", "#A e #B", "#A, #B e #C" — marca "Reúne" e linha do tempo de agrupamento (spec T18). */
export const formatPackageIdList = (ids: readonly number[]): string => {
  const labels = ids.map((id) => `#${id}`)
  if (labels.length <= 1) return labels.join('')
  return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`
}
